import { useCallback, useEffect, useState } from 'react';

/**
 * A router in thirty lines.
 *
 * The site has six pages and no dependencies, and pulling in a routing library to move between
 * six static pages would be the largest thing in the bundle. History API, one listener, done.
 *
 * Real paths rather than hashes, because these pages are meant to be indexed and linked to. In
 * production that needs the host to serve index.html for unknown paths; Vite's dev server
 * already does.
 */
export function useRoute(): [string, (to: string) => void] {
  const [path, setPath] = useState(() => window.location.pathname);

  useEffect(() => {
    const onPop = () => setPath(window.location.pathname);
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const navigate = useCallback((to: string) => {
    if (to === window.location.pathname) return;

    window.history.pushState({}, '', to);
    setPath(to);
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, []);

  return [path, navigate];
}

/**
 * An anchor that navigates without a reload.
 *
 * Still a real <a href>, so it can be middle-clicked, opened in a new tab, copied, and read by
 * a crawler. Only a plain left-click is intercepted — modified clicks belong to the browser.
 */
export function Link({
  to,
  children,
  className,
  onNavigate,
}: {
  to: string;
  children: React.ReactNode;
  className?: string;
  onNavigate: (to: string) => void;
}) {
  return (
    <a
      href={to}
      className={className}
      onClick={(event) => {
        if (
          event.defaultPrevented ||
          event.button !== 0 ||
          event.metaKey ||
          event.ctrlKey ||
          event.shiftKey ||
          event.altKey
        ) {
          return;
        }

        event.preventDefault();
        onNavigate(to);
      }}
    >
      {children}
    </a>
  );
}
